'use client';

import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import dynamic from 'next/dynamic';

// Syntax highlighting is useful once someone starts editing (or when content is
// already present), but its parser is expensive on an empty first paint.
const CodeEditor = dynamic(
  () => import('@uiw/react-textarea-code-editor').then((mod) => mod.default),
  { ssr: false }
);

interface TextareaCodeEditorProps {
  value: string;
  onChange: (value: string) => void;
  language?: string;
  placeholder?: string;
  height?: number | string;
  readOnly?: boolean;
  isDarkMode: boolean;
  ariaLabel?: string;
  forceDarkEditor?: boolean;
  forceDarkerBackground?: boolean;
}

const editorFont = '"JetBrains Mono", "Monaco", "Menlo", "Ubuntu Mono", "Consolas", "source-code-pro", monospace';

const TextareaCodeEditor = ({
  value,
  onChange,
  placeholder = '',
  height = 250,
  readOnly = false,
  isDarkMode,
  ariaLabel,
  forceDarkerBackground = false,
}: TextareaCodeEditorProps) => {
  const [isEnhanced, setIsEnhanced] = useState(() => Boolean(value));
  const heightStr = typeof height === 'number' ? `${height}px` : height;
  const bgColor = forceDarkerBackground ? 'var(--bg-subtle)' : 'var(--bg)';
  const label = ariaLabel || 'JSON editor';

  // URL examples hydrate the editor after the first client render. Enhance as
  // soon as that content arrives, without waiting for a focus event.
  useEffect(() => {
    if (value && !isEnhanced) setIsEnhanced(true);
  }, [value, isEnhanced]);

  return (
    <Box sx={{
      height: heightStr,
      overflowY: 'auto',
      width: '100%',
      backgroundColor: bgColor,
      position: 'relative',
      '& > div': { minHeight: '100%' },
    }}>
      {isEnhanced ? (
        <CodeEditor
          value={value}
          language="json"
          placeholder={placeholder}
          onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => onChange(event.target.value)}
          disabled={readOnly}
          padding={15}
          data-color-mode={isDarkMode ? 'dark' : 'light'}
          aria-label={label}
          autoFocus={!readOnly}
          style={{
            fontSize: 14,
            fontFamily: editorFont,
            backgroundColor: 'transparent',
            minHeight: '100%',
          }}
        />
      ) : (
        <textarea
          value={value}
          onChange={(event) => onChange(event.target.value)}
          onFocus={() => setIsEnhanced(true)}
          readOnly={readOnly}
          placeholder={placeholder}
          aria-label={label}
          spellCheck={false}
          style={{
            display: 'block',
            width: '100%',
            minHeight: '100%',
            resize: 'none',
            border: 0,
            outline: 0,
            padding: '15px',
            boxSizing: 'border-box',
            background: 'transparent',
            color: 'var(--fg)',
            caretColor: 'var(--fg)',
            fontSize: '14px',
            lineHeight: 1.5,
            fontFamily: editorFont,
          }}
        />
      )}
    </Box>
  );
};

export default TextareaCodeEditor;
